import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { Table } from "common/Table/Table"
import UserService from "services/user.service"
import {
  exportToCSV,
  formatDate,
  formatExportDate,
  type ExportColumnDef,
  parseExportNumber,
} from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import swal from "sweetalert"
import { CreateOperationModal, getOperationExpenses, getExpensesTotal } from "components/operations"
import { useRouter } from "hooks/useRouter"
import { Modal } from "common/Modal"
import { EvoFiltersBar } from "components/evo/EvoFiltersBar"
import { CompletionStatusControl } from "components/evo/CompletionStatusControl"
import {
  EvoButton,
  EvoFieldLabel,
  EvoInput,
  EvoSelect,
  EvoStatTile,
} from "components/evo"
import { usePersistedFilters } from "hooks/usePersistedFilters"
import {
  buildEvoQueryParams,
  formatAmountSubmitted,
  formatMoney,
  reviewedFromSubmission,
  submissionDateDisplay,
  submissionFromReviewed,
  setPersistedTripsTab,
  toDateInputValue,
} from "utils/evoTripsUi"

const service = new UserService()

const OPERATION_EXPORT_COLUMNS: ExportColumnDef[] = [
  { dbColName: "badgeId", title: "Badge ID" },
  { dbColName: "fullName", title: "Full Name" },
  { dbColName: "monthName", title: "Month" },
  { dbColName: "submissionDate", title: "Submission Date" },
  { dbColName: "amountSubmitted", title: "Amount Submitted", decimals: 2 },
  { dbColName: "approvedAmountToPay", title: "Approved Amount to Pay", decimals: 2 },
]

const confirmDelete = (title: string, onConfirm: () => void) => {
  swal({
    title,
    icon: "warning",
    className: "w-[500px]",
    buttons: {
      cancel: { text: "Cancel", visible: true },
      confirm: { text: "Delete", className: "bg-primary text-white" },
    },
    dangerMode: true,
  }).then((willDelete) => willDelete && onConfirm())
}

export const Operations = () => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
  })
  const [showCreate, setShowCreate] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedOperation, setSelectedOperation] = useState<any>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState({
    reviewed: "No",
    submissionDate: "",
    amountSubmitted: "",
  })
  const { filters, setFilters, clearFilters } = usePersistedFilters("operations-list")

  const { push } = useRouter()
  const { successMessage, errorMessage } = useNotifications()

  const fetchOperationsList = useCallback(() => {
    setLoading(true)
    service
      .getOperations(buildEvoQueryParams(pagination, filters))
      .then((resp) => {
        setData(resp.data.data ?? [])
        setTotalItems(resp.data.totalDocument ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [pagination, filters.fullName, filters.month, filters.year])

  useEffect(() => {
    fetchOperationsList()
  }, [fetchOperationsList])

  useEffect(() => {
    setPagination((p) => ({ ...p, pageNumber: 1 }))
  }, [filters.fullName, filters.month, filters.year])

  const handleChangeStatus = (id: string, status: "Paid" | "Not-Paid") => {
    setUpdatingStatusId(id)
    service
      .updateOperation(id, { tripStatus: status })
      .then((resp) => {
        if (resp.data.success) {
          successMessage("Operation status updated")
          setData((prev) =>
            prev.map((row) =>
              String(row._id) === String(id) ? { ...row, tripStatus: status } : row
            )
          )
        }
      })
      .catch(() => errorMessage("Failed to update status"))
      .finally(() => setUpdatingStatusId(null))
  }

  const handleEditClick = (row: any) => {
    setSelectedOperation(row)
    setEditFormData({
      reviewed: reviewedFromSubmission(row.operationSubmissionOpenOrClose),
      submissionDate: toDateInputValue(row.submissionDate),
      amountSubmitted:
        row.amountSubmitted != null && row.amountSubmitted !== ""
          ? String(row.amountSubmitted)
          : "",
    })
    setShowEditModal(true)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedOperation || isUpdating) return

    const updateData: Record<string, unknown> = {
      reviewed: editFormData.reviewed,
      operationSubmissionOpenOrClose: submissionFromReviewed(editFormData.reviewed),
    }
    if (editFormData.submissionDate) {
      updateData.submissionDate = editFormData.submissionDate
    }
    if (editFormData.amountSubmitted) {
      updateData.amountSubmitted = parseFloat(editFormData.amountSubmitted) || 0
    }

    setIsUpdating(true)
    service
      .updateOperation(selectedOperation._id, updateData)
      .then((resp) => {
        if (resp.data.success) {
          successMessage("Operation updated")
          setData((prev) =>
            prev.map((row) =>
              row._id === selectedOperation._id ? { ...row, ...updateData } : row
            )
          )
          setShowEditModal(false)
          setSelectedOperation(null)
        }
      })
      .catch(() => errorMessage("Failed to update operation"))
      .finally(() => setIsUpdating(false))
  }

  const operationColumns = useMemo(
    () => [
      { key: "badgeId", dbColName: "badgeId", title: "Badge ID" },
      { key: "fullName", dbColName: "fullName", title: "Full Name", isSort: true },
      { key: "monthName", dbColName: "monthName", title: "Month" },
      {
        key: "submissionDate",
        dbColName: "submissionDate",
        title: "Submission Date",
        render: (date: string) => submissionDateDisplay(date),
      },
      {
        key: "amountSubmitted",
        title: "Amount Submitted",
        render: (row: any) => {
          const { text, incomplete } = formatAmountSubmitted(row.amountSubmitted)
          return (
            <span className={incomplete ? "text-red-600 font-medium" : ""}>{text}</span>
          )
        },
      },
      {
        key: "approvedAmountToPay",
        dbColName: "approvedAmountToPay",
        title: "Approved Amount to Pay",
        render: (value: number) => formatMoney(value),
      },
      {
        key: "actions",
        title: "Actions",
        freeze: true,
        freezeIndex: "right-0",
        render: (item: any) => (
          <div className="flex items-center gap-2">
            <CompletionStatusControl
              rowId={String(item._id)}
              status={item.tripStatus}
              disabled={updatingStatusId === item._id}
              onChange={(status) => handleChangeStatus(item._id, status)}
            />
            <button
              type="button"
              onClick={() => handleEditClick(item)}
              className="rounded p-1 text-blue-600 transition hover:bg-blue-50 hover:text-blue-800"
              title="Edit operation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => {
                setPersistedTripsTab("Operations")
                push(`/operation-details/${item._id}`)
              }}
              className="text-blue-500 py-1 rounded-md hover:text-blue-700"
              title="Open operation details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() =>
                confirmDelete("Delete this operation and all its receipts?", () => {
                  service
                    .deleteOperation(item._id)
                    .then(() => {
                      successMessage("Operation deleted")
                      fetchOperationsList()
                    })
                    .catch(() => errorMessage("Failed to delete"))
                })
              }
              className="text-red-500 py-1 rounded-md hover:text-red-700"
              title="Delete operation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    [fetchOperationsList, push, updatingStatusId]
  )

  const handleExport = async () => {
    setExporting(true)
    try {
      const resp = await service.getOperations({
        ...buildEvoQueryParams(pagination, filters),
        isDownload: "true",
      })
      const list = resp.data.data ?? []
      const rows = list.map((row: any) => ({
        badgeId: String(row.badgeId ?? ""),
        fullName: String(row.fullName ?? ""),
        monthName: String(row.monthName ?? ""),
        submissionDate: formatExportDate(row.submissionDate),
        amountSubmitted: parseExportNumber(row.amountSubmitted) ?? "",
        approvedAmountToPay: parseExportNumber(row.approvedAmountToPay) ?? 0,
      }))
      exportToCSV(rows, "operations", { columns: OPERATION_EXPORT_COLUMNS })
    } catch (e) {
      console.error(e)
    } finally {
      setExporting(false)
    }
  }

  return (
    <Fragment>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="grid max-w-xs grid-cols-1 gap-3 sm:grid-cols-2">
          <EvoStatTile
            label="Operations"
            value={loading ? "—" : totalItems}
            hint="Matches current filters"
          />
        </div>
        <EvoButton type="button" onClick={() => setShowCreate(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Create operation
        </EvoButton>
      </div>

      <Table
        data={data}
        loadingData={loading}
        setData={setData}
        totalItems={totalItems}
        columns={operationColumns}
        pagination={pagination}
        setPagination={setPagination}
        exportButton={true}
        handleExport={handleExport}
        exporting={exporting}
        showSearch={false}
        filters={
          <EvoFiltersBar
            filters={filters}
            onChange={(patch) => setFilters(patch)}
            onClear={clearFilters}
          />
        }
      />

      <CreateOperationModal
        showModal={showCreate}
        setShowModal={setShowCreate}
        onSuccess={fetchOperationsList}
      />

      <Modal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        title="Edit Operation"
        onClose={() => {
          setSelectedOperation(null)
          setIsUpdating(false)
          setEditFormData({ reviewed: "No", submissionDate: "", amountSubmitted: "" })
        }}
        width="w-full md:w-1/3"
      >
        <form onSubmit={handleEditSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Reviewed</EvoFieldLabel>
            <EvoSelect
              value={editFormData.reviewed}
              onChange={(e) => setEditFormData({ ...editFormData, reviewed: e.target.value })}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </EvoSelect>
          </div>
          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Date submitted</EvoFieldLabel>
            <EvoInput
              type="date"
              value={editFormData.submissionDate}
              onChange={(e) => setEditFormData({ ...editFormData, submissionDate: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Amount submitted</EvoFieldLabel>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-evo-subtle">$</span>
              <EvoInput
                type="number"
                step="0.01"
                min="0"
                value={editFormData.amountSubmitted}
                onChange={(e) => setEditFormData({ ...editFormData, amountSubmitted: e.target.value })}
                className="pl-7"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2.5 border-t border-evo-border pt-2">
            <EvoButton type="button" variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </EvoButton>
            <EvoButton type="submit" loading={isUpdating} disabled={isUpdating}>
              {isUpdating ? "Updating…" : "Save changes"}
            </EvoButton>
          </div>
        </form>
      </Modal>
    </Fragment>
  )
}
